import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, Trash2, Loader2, Plus, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Plyx admin — manage featured creators that appear on /plyx (landing).
 * Manual pins always render before auto-engagement creators. Cap = 12 pins.
 */
const AdminPlyxFeatured = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [handle, setHandle] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/plyx/admin/login", { replace: true, state: { from: "/admin/plyx/featured" } });
      return;
    }
    if (user.role !== "admin") {
      navigate("/", { replace: true });
      return;
    }
    load();
  }, [user, authLoading]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/plyx/featured-creators");
      setPins(r.data.pins || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const pin = async (e) => {
    e.preventDefault();
    const clean = handle.trim().replace(/^@/, "");
    if (!clean) {
      toast.error("Enter a Plyx handle");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/plyx/featured-creators", { handle: clean, note: note.trim() || null });
      toast.success(`@${clean} pinned to /plyx`);
      setHandle("");
      setNote("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not pin creator");
    } finally {
      setSubmitting(false);
    }
  };

  const unpin = async (h) => {
    try {
      await api.delete(`/admin/plyx/featured-creators/${h}`);
      toast.success(`@${h} unpinned`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not unpin");
    }
  };

  if (loading) {
    return (
      <div className="container-narrow py-20 text-center" data-testid="admin-featured-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="container-narrow py-20 text-center text-muted-foreground" data-testid="admin-featured-error">
        <AlertCircle className="w-8 h-8 mx-auto mb-4 opacity-60" />
        {error}
      </div>
    );
  }

  return (
    <div className="container-narrow py-10" data-testid="admin-featured-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-overline text-[#C05A3C] mb-2">Plyx · admin</div>
          <h1 className="font-display text-4xl">Featured creators</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Hand-pick up to 12 creators to spotlight on the public Plyx landing page (<code className="text-xs">/plyx</code>).
            If you pin fewer than 5, the rest auto-fill from top engagement (last 30 days).
            Pinned creators always render first.
          </p>
        </div>
      </div>

      <form
        onSubmit={pin}
        className="mb-8 p-5 rounded-lg border border-black/10 bg-[#F3EFE9]/60 flex flex-wrap gap-3 items-end"
        data-testid="admin-featured-pin-form"
      >
        <div className="flex-1 min-w-[160px]">
          <label className="label-overline mb-1.5 block">Plyx handle</label>
          <input
            type="text"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="@tobe"
            data-testid="admin-featured-handle-input"
            className="w-full px-3 py-2 rounded-md border border-black/15 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/40"
          />
        </div>
        <div className="flex-[2] min-w-[240px]">
          <label className="label-overline mb-1.5 block">Note (optional)</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            placeholder="Why is this creator featured? Shown as the card subtitle"
            data-testid="admin-featured-note-input"
            className="w-full px-3 py-2 rounded-md border border-black/15 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/40"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="admin-featured-pin-submit"
          className="px-4 py-2 rounded-md bg-[#C05A3C] hover:bg-[#D46B4E] text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Pin creator
        </button>
      </form>

      {pins.length === 0 ? (
        <div
          className="p-8 rounded-lg border border-dashed border-black/15 text-center text-sm text-muted-foreground"
          data-testid="admin-featured-empty"
        >
          <Star className="w-6 h-6 mx-auto mb-2 opacity-50" />
          No pinned creators yet. The /plyx landing page is currently auto-filling from top engagement.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-featured-list">
          {pins.map((pin) => {
            const c = pin.creator;
            return (
              <li
                key={pin.pin_id}
                data-testid={`admin-featured-row-${pin.handle}`}
                className="flex items-center gap-4 p-4 rounded-lg border border-black/10 bg-white"
              >
                {c?.avatar_url ? (
                  <img src={c.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
                  >
                    {(c?.display_name || pin.handle || "?")[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    @{pin.handle}
                    {c?.is_removed && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#B84A28]/15 text-[#B84A28]">
                        removed
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {c?.display_name || "—"} · {(c?.follower_count || 0).toLocaleString()} followers · {(c?.video_count || 0)} videos
                  </div>
                  {pin.note && (
                    <div className="text-xs text-foreground/70 mt-1 line-clamp-1">“{pin.note}”</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => unpin(pin.handle)}
                  data-testid={`admin-featured-unpin-${pin.handle}`}
                  className="px-3 py-2 rounded-md border border-black/10 text-xs text-[#B84A28] hover:bg-[#B84A28]/5 inline-flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Unpin
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AdminPlyxFeatured;
